import apiClient from "api";
import { ApiError } from "api/client";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { gettext } from "i18n";

/**
 * Multi-factor authentication (mg-core mg.auth.mfa_views): second factors,
 * step-up verification of sensitive actions, and confirming logins from
 * unusual places.
 */

export const METHOD_TOTP = "totp";
export const METHOD_PASSKEY = "passkey";
export const METHOD_EMAIL = "email";
export const METHOD_BACKUP = "backup";

export type MfaMethodKind = "totp" | "passkey" | "email";
export type VerificationMethod = MfaMethodKind | "backup";

// What a session may only do for now (mg.auth.restrictions)
export const RESTRICTION_MFA_SETUP = "mfa_setup";
export const RESTRICTION_LOCATION = "location_confirmation";

export const METHOD_LABELS: Record<VerificationMethod, string> = {
  totp: "Authenticator app",
  passkey: "Passkey",
  email: "Email codes",
  backup: "Backup code",
};

export const METHOD_DESCRIPTIONS: Record<MfaMethodKind, string> = {
  totp: "Codes from an app on your phone, e.g. Google Authenticator, Microsoft Authenticator or Authy.",
  passkey:
    "Your fingerprint, face or screen lock, or a security key such as a YubiKey.",
  email: "Codes sent to your email address each time you log in.",
};

export interface MfaRequirementReason {
  kind: "global_admin" | "user" | "organization";
  organization_id?: number;
  organization_name?: string;
  enforced_by_global_admin?: boolean;
}

export interface MfaMethod {
  id: number;
  kind: MfaMethodKind;
  label: string;
  created_at: string | null;
  last_used_at: string | null;
  // Whether the method may still be used to meet a requirement of MFA
  allowed: boolean;
}

export interface MfaStatus {
  enabled: boolean;
  required: boolean;
  required_reasons: MfaRequirementReason[];
  setup_required: boolean;
  allowed_methods: MfaMethodKind[];
  passkeys_available: boolean;
  email_allowed: boolean;
  methods: MfaMethod[];
  preferred_method: MfaMethodKind | null;
  backup_codes_remaining: number;
  recently_verified: boolean;
  verified_at: string | null;
  step_up_validity_seconds: number;
  email_hint: string;
}

// What the login page needs to ask for the second factor
export interface MfaLoginChallenge {
  challenge: string;
  methods: VerificationMethod[];
  preferred: VerificationMethod | null;
  email_sent: boolean;
  email_hint: string | null;
}

export interface OrganizationMfaSettings {
  organization_id: number;
  mfa_required: boolean;
  mfa_enforced: boolean;
  mfa_app_only: boolean;
  can_enforce: boolean;
}

export interface LocationDetails {
  email: string;
  country: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  expired: boolean;
  confirmed: boolean;
  session_active: boolean;
}

export const describeRequirement = (reason: MfaRequirementReason): string => {
  if (reason.kind === "global_admin") {
    return gettext("MFA is mandatory for global admins.");
  }
  if (reason.kind === "user") {
    return gettext("An administrator has made MFA mandatory for your account.");
  }
  return gettext(
    "Organization %1 requires MFA from its members.",
    reason.organization_name ?? "",
  );
};

/** Passkeys need a browser that supports them (the mobile app may not) */
export const passkeysSupported = (): boolean => {
  try {
    return browserSupportsWebAuthn();
  } catch (e) {
    return false;
  }
};

/** Whether the action was refused until the second factor is confirmed */
export const isStepUpRequired = (e: unknown): boolean =>
  e instanceof ApiError && !!e.data?.step_up_required;

const post = (path: string, data: Record<string, any> = {}) =>
  apiClient.request(path, { method: "POST", data });

// Completing a login

export const requestLoginEmailCode = (challenge: string) =>
  post("/auth/mfa/login/email-code", { challenge });

export const verifyLogin = async (
  challenge: string,
  method: VerificationMethod,
  code?: string,
) => {
  let credential;
  if (method === METHOD_PASSKEY) {
    const { options } = await post("/auth/mfa/login/passkey-options", {
      challenge,
    });
    credential = await startAuthentication(options);
  }
  return post("/auth/mfa/login/verify", {
    challenge,
    method,
    code,
    credential,
  });
};

// The user's own settings

export const loadMfaStatus = async (): Promise<MfaStatus> =>
  (await apiClient.request("/auth/mfa/status")) as MfaStatus;

export const beginTotp = async (): Promise<{
  method_id: number;
  secret: string;
  otpauth_uri: string;
}> => (await post("/auth/mfa/totp/begin")) as any;

export const confirmTotp = (methodId: number, code: string, label: string) =>
  post("/auth/mfa/totp/confirm", { method_id: methodId, code, label });

export const addPasskey = async (label: string) => {
  const { options } = await post("/auth/mfa/passkey/begin");
  const credential = await startRegistration(options);
  return post("/auth/mfa/passkey/finish", { credential, label });
};

export const beginEmailMethod = () => post("/auth/mfa/email/begin");

export const confirmEmailMethod = (code: string) =>
  post("/auth/mfa/email/confirm", { code });

export const deleteMethod = (methodId: number) =>
  post("/auth/mfa/methods/delete", { method_id: methodId });

export const renameMethod = (methodId: number, label: string) =>
  post("/auth/mfa/methods/rename", { method_id: methodId, label });

export const setPreferredMethod = (method: MfaMethodKind) =>
  post("/auth/mfa/preferred", { method });

export const regenerateBackupCodes = async (): Promise<string[]> =>
  (await post("/auth/mfa/backup-codes")).backup_codes;

export const disableMfa = () => post("/auth/mfa/disable");

// Step-up verification

export const beginStepUp = (method: VerificationMethod) =>
  post("/auth/mfa/step-up/begin", { method });

export const verifyStepUp = async (
  method: VerificationMethod,
  code?: string,
) => {
  let credential;
  if (method === METHOD_PASSKEY) {
    const { options } = await beginStepUp(method);
    credential = await startAuthentication(options);
  }
  return post("/auth/mfa/step-up/verify", { method, code, credential });
};

// Administration

export const resetUserMfa = (userId: number) =>
  post("/auth/mfa/admin/reset", { user_id: userId });

export const requireUserMfa = (userId: number, required: boolean) =>
  post("/auth/mfa/admin/require", { user_id: userId, required });

export const loadOrganizationMfa = async (
  organizationId: number,
): Promise<OrganizationMfaSettings> =>
  (await apiClient.request(
    `/auth/mfa/organization?organization_id=${organizationId}`,
  )) as OrganizationMfaSettings;

export const updateOrganizationMfa = async (
  organizationId: number,
  settings: Partial<OrganizationMfaSettings>,
): Promise<OrganizationMfaSettings> =>
  (await post("/auth/mfa/organization/update", {
    organization_id: organizationId,
    ...settings,
  })) as OrganizationMfaSettings;

// Confirming logins from unusual places

export const loadLocationDetails = async (
  token: string,
): Promise<LocationDetails> =>
  (await post("/auth/location/details", { token })) as LocationDetails;

export const confirmLocation = (token: string) =>
  post("/auth/location/confirm", { token });

export const denyLocation = (token: string) =>
  post("/auth/location/deny", { token });

export const resendLocationEmail = () => post("/auth/location/resend");

/** The backup codes as a text file to save */
export const downloadBackupCodes = (codes: string[]) => {
  const text = [
    gettext("Mittausguru backup codes"),
    gettext(
      "Each code can be used once, if you cannot use your other sign-in methods.",
    ),
    "",
    ...codes,
    "",
  ].join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "mittausguru-backup-codes.txt";
  link.click();
  URL.revokeObjectURL(url);
};
